import styled from '@emotion/styled/macro';
import { Variables } from '../variables';

const V = Variables.ThemeLight;

/**
 * The three tones a notice can take, each a pale surface with the dark end of its own ramp on top.
 *
 * Pairing two `*Mild*` or two `*Medium*` tokens is the contrast trap NS-2 describes — they are both surface ramps, and
 * a notice built from them is unreadable while the build stays green. Warning reuses exactly the pair the HALTED badge
 * in `payer-columns` already uses, so a halt notice and the halted rows under it read as one thing.
 */
const TONES = {
    warning: { background: V.WarningMild3, foreground: V.WarningInverted4, icon: V.WarningConstant6 },
    attention: { background: V.AttentionMild3, foreground: V.AttentionInverted3, icon: V.AttentionConstant6 },
    info: { background: V.PrimaryMild2, foreground: V.PrimaryInverted3, icon: V.PrimaryConstant6 },
};

export type NoticeTone = keyof typeof TONES;

export const Wrapper = styled.div<{ tone: NoticeTone }>`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 12px;
    line-height: 1.5;
    background: ${({ tone }) => TONES[tone].background};
    color: ${({ tone }) => TONES[tone].foreground};
`;

/**
 * Holds the icon on the first line of text rather than centred against a paragraph.
 *
 * The icon takes the bright end of the ramp while the text keeps the dark one: it is the mark that catches the eye,
 * and it carries no meaning the sentence does not — so it can be vivid without having to be readable as text.
 */
export const IconSlot = styled.span<{ tone: NoticeTone }>`
    display: inline-flex;
    flex: none;
    padding-top: 1px;
    color: ${({ tone }) => TONES[tone].icon};
`;

export const Body = styled.span`
    min-width: 0;
`;
